import { getId } from 'customer-data-objects/model/ImmutableModel';
import { FrameworkDataTable } from 'framework-data-table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import globalNavHeight from 'nav-meta/global-nav-height';
import { useCallback } from 'react';
import { NavMarker } from 'react-rhumb';
import { PAGE_SIZE_OPTIONS } from '../../constants/table';
import BulkActionsButtons from '../bulkActions/BulkActions';
import { View } from 'foundations-components/components/View';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import ErrorBoundary from '../ErrorBoundary';

// TODO Fix height/overflow issues when window is narrow
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NAV_HEIGHT = parseInt(globalNavHeight, 10);
// Magic constants measured via devtools
const HEADER_HEIGHT = 160;
const FILTER_BAR_HEIGHT = 80;
const TOTAL_HEIGHT_OFFSET = NAV_HEIGHT + HEADER_HEIGHT + FILTER_BAR_HEIGHT;
export const MAX_TABLE_HEIGHT = `calc(100vh - ${TOTAL_HEIGHT_OFFSET}px)`;
const TABLE_MIN_HEIGHT = '360px';
export const normalTableStyle = {
  height: MAX_TABLE_HEIGHT,
  minHeight: TABLE_MIN_HEIGHT,
  // https://issues.hubspotcentral.com/browse/CRM-36953 - Occasionally, during
  // scrolling, something overflows the table and causes a body scrollbar to
  // show. This causes jumpiness. To hide it, we'll hide anything that
  // overflows our container.
  overflow: 'hidden'
};
const TasksTableV2 = ({
  hasGate,
  hasScope,
  headerLeftSlot,
  headerRightSlot,
  noRowsSlot,
  onColumnWidthChange,
  onColumnsChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  pageIndex,
  pageSize,
  columns,
  sorts,
  tasks,
  totalRowCount,
  loading
}) => {
  const handleColumnOrderChange = useCallback(({
    columnName: changedColumnName,
    nextIndex
  }) => {
    const filteredCols = columns.filter(({
      columnName
    }) => columnName !== changedColumnName).map(({
      columnName
    }) => columnName);
    filteredCols.splice(nextIndex, 0, changedColumnName);
    onColumnsChange(filteredCols);
  }, [columns, onColumnsChange]);

  // Memoize the error component to prevent unnecessary re-renders
  const renderFrameworkDataTableError = useCallback(() => {
    return /*#__PURE__*/_jsx(View, {
      as: "div",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "crm-tasks-ui.errorBoundary.frameworkDataTable.title"
        }),
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "crm-tasks-ui.errorBoundary.frameworkDataTable.message"
          })
        })
      })
    });
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [tasks.length > 0 ? /*#__PURE__*/_jsx(NavMarker, {
      name: "TASKS_TABLE_LOADED"
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "TASKS_TABLE_EMPTY"
    }), /*#__PURE__*/_jsx(View, {
      overflow: "hidden",
      blockSize: MAX_TABLE_HEIGHT,
      minBlockSize: TABLE_MIN_HEIGHT,
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        fallback: renderFrameworkDataTableError(),
        boundaryName: "task-table-v2-framework-data-table",
        children: /*#__PURE__*/_jsx(FrameworkDataTable, {
          BulkActionsComponent: BulkActionsButtons,
          columns: columns,
          getRowId: task => String(getId(task)),
          hasGate: hasGate,
          hasScope: hasScope,
          headerLeftSlot: headerLeftSlot,
          headerRightSlot: headerRightSlot,
          noRowsSlot: noRowsSlot,
          pageable: true,
          pageIndex: pageIndex,
          pageSize: pageSize,
          pageSizes: PAGE_SIZE_OPTIONS,
          onColumnOrderChange: handleColumnOrderChange,
          onPageIndexChange: ({
            nextPageIndex
          }) => onPageIndexChange(nextPageIndex),
          onPageSizeChange: ({
            nextPageSize
          }) => onPageSizeChange(nextPageSize),
          onColumnWidthChange: onColumnWidthChange,
          onSortsChange: onSortsChange,
          reorderable: true,
          resizable: true,
          rows: tasks,
          sorts: sorts,
          totalRowCount: totalRowCount,
          loading: loading,
          disableScrollReset: true
        })
      })
    })]
  });
};
TasksTableV2.displayName = 'TasksTableV2';
export default TasksTableV2;